import React, { useMemo, useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { X, ChevronDown } from 'lucide-react';
import { MAX_SELECTED_VERSIONS } from '../utils/versionFilter';

/**
 * Мультивыбор версий (client_ver / client_ver_i) в стиле селектора кампаний.
 *
 * Список опций приходит отсортированным (новые первыми). Выбранные версии,
 * которых нет в списке (например, пришли из ссылки или относятся к другому
 * проекту), остаются видимыми и снимаемыми — иначе фильтр нельзя было бы
 * очистить, не видя его значений.
 */
const VersionFilter = ({ title, options = [], selected = [], onChange, loading = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const allVersions = useMemo(() => {
    const known = new Set(options.map(option => option.value));
    const extra = selected.filter(version => !known.has(version));
    return [...extra, ...options.map(option => option.value)];
  }, [options, selected]);

  const filteredVersions = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    return needle ? allVersions.filter(version => version.toLowerCase().includes(needle)) : allVersions;
  }, [allVersions, searchText]);

  const handleToggle = (version) => {
    if (selected.includes(version)) {
      onChange(selected.filter(item => item !== version));
      return;
    }
    if (selected.length >= MAX_SELECTED_VERSIONS) return;
    onChange([...selected, version]);
  };

  const handleSelectAll = () => {
    onChange([...new Set([...selected, ...filteredVersions])].slice(0, MAX_SELECTED_VERSIONS));
  };

  const handleClearAll = () => onChange([]);

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">{title}</h3>

      {loading ? (
        <div className="text-xs text-muted-foreground py-2">Загрузка...</div>
      ) : (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full h-9 justify-between text-xs">
              <span className="truncate">
                {selected.length === 0 ? 'Все версии' : `Выбрано ${selected.length} / ${MAX_SELECTED_VERSIONS}`}
              </span>
              <ChevronDown className="h-3 w-3 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[320px] max-w-[90vw] max-h-[60vh] flex flex-col p-3" align="start">
            <div className="flex-shrink-0 space-y-3 mb-3">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Поиск версий..."
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  className="h-8 text-xs pr-8"
                />
                {searchText && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                    onClick={() => setSearchText('')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleSelectAll} className="flex-1 h-8 text-xs">
                  Выбрать все
                </Button>
                <Button variant="outline" size="sm" onClick={handleClearAll} className="flex-1 h-8 text-xs">
                  Снять все
                </Button>
              </div>

              {selected.length >= MAX_SELECTED_VERSIONS && (
                <div className="text-xs text-amber-600">Максимум {MAX_SELECTED_VERSIONS} версий</div>
              )}
            </div>

            <div className="flex-1 min-h-0 border rounded-md">
              <ScrollArea className="h-full max-h-[40vh]">
                <div className="p-2 space-y-1">
                  {filteredVersions.length === 0 ? (
                    <div className="text-xs text-muted-foreground py-2 text-center">
                      {searchText ? 'Ничего не найдено' : 'Нет доступных версий'}
                    </div>
                  ) : (
                    filteredVersions.map(version => {
                      const isSelected = selected.includes(version);
                      const isDisabled = !isSelected && selected.length >= MAX_SELECTED_VERSIONS;

                      return (
                        <div
                          key={version}
                          className={`flex items-center space-x-2 p-2 rounded-md hover:bg-accent ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          onClick={() => !isDisabled && handleToggle(version)}
                        >
                          <Checkbox
                            checked={isSelected}
                            disabled={isDisabled}
                            onCheckedChange={() => handleToggle(version)}
                            className="h-4 w-4"
                          />
                          <span className="text-sm flex-1 truncate" title={version}>
                            {version}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default VersionFilter;
